import React from 'react';
import { useIntl } from 'react-intl';

import { FilterableSelect, Option } from 'carbon-react/lib/components/select';

import { LiveReportList } from '@futrli-sdk/predict';

import { EmptySelect } from '../../empty-select/empty-select';
import { TEMPLATE_FORM_FIELD_IDS } from '../constants';
import { CreateTemplateSidebarMode } from '../create-template-sidebar';

export interface TemplateSourceSelectProps {
  liveReports: LiveReportList[];
  selectedReportId: string;
  onSelectedReportIdChange: (value: string) => void;
  onNavigateToCreateReport: () => void;
  error?: string;
  onBlur?: () => void;
  mode?: CreateTemplateSidebarMode;
  disabled?: boolean;
}

export const TemplateSourceSelect = ({
  liveReports,
  selectedReportId,
  onSelectedReportIdChange,
  onNavigateToCreateReport,
  error,
  onBlur,
  mode = 'report',
  disabled = false
}: TemplateSourceSelectProps) => {
  const intl = useIntl();
  const isDashboard = mode === 'dashboard';

  if (liveReports.length > 0) {
    return (
      <FilterableSelect
        id={TEMPLATE_FORM_FIELD_IDS.REPORT_SELECT}
        label={
          isDashboard
            ? intl.formatMessage({ id: 'reporting.select_dashboard_for_template', defaultMessage: 'Select a dashboard to turn into a template' })
            : intl.formatMessage({ id: 'reporting.select_report_for_template', defaultMessage: 'Select a report to turn into a template' })
        }
        placeholder={intl.formatMessage({
          id: 'reporting.select_one',
          defaultMessage: 'Select one'
        })}
        inputHint={
          isDashboard
            ? intl.formatMessage({ id: 'reporting.type_to_search_dashboards', defaultMessage: 'Type to search dashboards' })
            : intl.formatMessage({ id: 'reporting.type_to_search_reports', defaultMessage: 'Type to search reports' })
        }
        value={selectedReportId}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => onSelectedReportIdChange(e.target.value as string)}
        mb={0}
        listMaxHeight={340}
        error={error}
        onBlur={onBlur}
        disabled={disabled}
        required
      >
        {liveReports.map(r => (
          <Option key={r.id} value={r.id} text={r.name} />
        ))}
      </FilterableSelect>
    );
  }

  return (
    <EmptySelect
      id={TEMPLATE_FORM_FIELD_IDS.REPORT_SELECT_EMPTY}
      label={
        isDashboard
          ? intl.formatMessage({ id: 'reporting.select_dashboard_for_template', defaultMessage: 'Select a dashboard to turn into a template' })
          : intl.formatMessage({ id: 'reporting.select_report_for_template', defaultMessage: 'Select a report to turn into a template' })
      }
      placeholder={intl.formatMessage({
        id: 'reporting.select_one',
        defaultMessage: 'Select one'
      })}
      emptyText={
        isDashboard
          ? intl.formatMessage({ id: 'reporting.no_dashboards_available', defaultMessage: 'No dashboards available' })
          : intl.formatMessage({ id: 'reporting.no_reports_available', defaultMessage: 'No reports available' })
      }
      createText={
        isDashboard
          ? intl.formatMessage({ id: 'reporting.create_a_dashboard', defaultMessage: 'Create a dashboard' })
          : intl.formatMessage({ id: 'reporting.create_a_report', defaultMessage: 'Create a report' })
      }
      onCreateAction={onNavigateToCreateReport}
      error={error}
    />
  );
};
